import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Container } from "reactstrap";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import AppBreadcrumb from "../../../Components/Common/AppBreadcrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
import { useProfile } from "../../../Components/Hooks/UserHooks";
import { useCurrencyFormatter } from "../../../Components/Hooks/useCurrencyFormatter";
import {
  deleteClient as onDeleteClient,
  getClients as onGetClients,
  getClientStats as onGetClientStats,
  getPhases as onGetPhases,
} from "../../../slices/thunks";
import { selectPhases } from "../../../slices/phases/selectors";
import { selectClientStats } from "../../../slices/clientStats/selectors";

import ClientModal from "../ClientModal/ClientModal";
import ClientStatTiles from "./components/ClientStatTiles";
import ClientFilterBar from "./components/ClientFilterBar";
import ClientsTable from "./components/ClientsTable";
import ClientPaginationFooter from "./components/ClientPaginationFooter";
import {
  readParamsFromSearch,
  buildQueryString,
  paramsEqual,
  DEFAULT_ORDERING,
  DEFAULT_BUYER_TYPE,
} from "./utils/filterParams";

const SEARCH_DEBOUNCE_MS = 500;

const selectClientProperties = createSelector(
  (state) => state.Clients,
  (client) => ({
    clients: client.clients,
    count: client.count,
    totalPages: client.totalPages,
    currentPage: client.currentPage,
    pageSize: client.pageSize,
    nextPage: client.nextPage,
    previousPage: client.previousPage,
    isClientSuccess: client.isClientSuccess,
    loading: client.loading,
  })
);

const ClientsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAdmin, isEditor } = useProfile();
  const { formatCurrency } = useCurrencyFormatter();
  const canDelete = isAdmin || isEditor;

  const {
    clients,
    count,
    totalPages,
    currentPage,
    pageSize,
    nextPage,
    previousPage,
    isClientSuccess,
    loading,
  } = useSelector(selectClientProperties);
  const phases = useSelector(selectPhases);
  const stats = useSelector(selectClientStats);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
  const initialFilterParams = useMemo(() => readParamsFromSearch(searchParams), []);

  const [filterParams, setFilterParams] = useState(initialFilterParams);
  const [page, setPage] = useState(initialPage);
  const [searchInput, setSearchInput] = useState(initialFilterParams.search || "");

  const [clientModal, setClientModal] = useState(false);
  const [isExportCSV, setIsExportCSV] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const clientRef = useRef(null);

  // One-way URL sync: filterParams/page are the source of truth; the URL is
  // only ever rewritten to match them (same pattern as Contract/List).
  useEffect(() => {
    const next = buildQueryString(filterParams, page);
    const currentStr = searchParams.toString();
    const nextStr = next.toString();
    if (currentStr !== nextStr) {
      setSearchParams(next, { replace: true });
    }
  }, [filterParams, page, searchParams, setSearchParams]);

  // buyer_type is also driven externally, by the sidebar's Natural/Legal
  // Clients links — they navigate to this same route with a different
  // ?buyer_type=, which React Router does not remount for (same path, just
  // a different query string). The one-way sync above only ever writes
  // state -> URL, so without this, clicking the other kind's link changes
  // the URL but the page keeps showing the previously-loaded kind. Mirrors
  // the design's own "switching kind clears every other filter" behavior.
  useEffect(() => {
    const urlBuyerType = searchParams.get("buyer_type");
    const normalized = urlBuyerType ? urlBuyerType.toUpperCase() : DEFAULT_BUYER_TYPE;
    let changed = false;
    setFilterParams((prev) => {
      if (prev.buyer_type === normalized) return prev;
      changed = true;
      return { buyer_type: normalized, ordering: DEFAULT_ORDERING };
    });
    if (changed) setSearchInput("");
  }, [searchParams.get("buyer_type")]);

  // Debounce the search box into filterParams.search (500ms).
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilterParams((prev) => {
        const trimmed = searchInput.trim();
        if ((prev.search || "") === trimmed) return prev;
        const next = { ...prev };
        if (trimmed) next.search = trimmed;
        else delete next.search;
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset to page 1 whenever filters actually change (skip the initial mount).
  const skipFirstFilterChange = useRef(true);
  useEffect(() => {
    if (skipFirstFilterChange.current) {
      skipFirstFilterChange.current = false;
      return;
    }
    setPage(1);
  }, [filterParams]);

  useEffect(() => {
    dispatch(onGetPhases()).catch((err) => console.error("Failed to fetch phases:", err));
  }, [dispatch]);

  // List fetch — includes payment_status/phase/linked/search/ordering/page.
  const fetchedFilterParams = useRef(filterParams);
  useEffect(() => {
    if (fetchedFilterParams.current !== filterParams && page !== 1) return;
    fetchedFilterParams.current = filterParams;
    dispatch(onGetClients({ ...filterParams, page, page_size: 20 })).catch((err) =>
      console.error("Failed to fetch clients:", err)
    );
  }, [dispatch, page, filterParams]);

  // Stats fetch — scoped to buyer_type only (see ClientStatTiles/ClientViewSet.stats).
  const statsParams = useMemo(
    () => ({ buyer_type: filterParams.buyer_type }),
    [filterParams.buyer_type]
  );
  useEffect(() => {
    dispatch(onGetClientStats(statsParams)).catch((err) =>
      console.error("Failed to fetch client stats:", err)
    );
  }, [dispatch, statsParams]);

  const updateFilter = useCallback((key, value) => {
    setFilterParams((prev) => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return paramsEqual(prev, next) ? prev : next;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setFilterParams((prev) => ({ buyer_type: prev.buyer_type, ordering: DEFAULT_ORDERING }));
  }, []);

  const hasAnyFilter = Boolean(
    searchInput || filterParams.payment_status || filterParams.phase || filterParams.linked
  );

  const handleRowClick = useCallback((id) => navigate(`/client-details/${id}`), [navigate]);

  const handleDeleteRequest = useCallback((client) => {
    clientRef.current = client;
    setDeleteModal(true);
  }, []);

  const handleDeleteClient = useCallback(async () => {
    const client = clientRef.current;
    if (!client || isDeleting) return;
    try {
      setIsDeleting(true);
      await dispatch(onDeleteClient(client.id)).unwrap();
      setDeleteModal(false);
      dispatch(onGetClients({ ...filterParams, page, page_size: 20 }));
      dispatch(onGetClientStats(statsParams));
    } catch (err) {
      console.error("Error deleting client:", err);
    } finally {
      setIsDeleting(false);
    }
  }, [dispatch, isDeleting, filterParams, page, statsParams]);

  const hasClients = Array.isArray(clients) && clients.length > 0;
  const emptyFromFilters = isClientSuccess && !hasClients && count === 0 && hasAnyFilter;
  const kindLabel = filterParams.buyer_type === "LEGAL" ? "Legal entities" : "Natural persons";

  const headline = stats
    ? `${stats.all?.count ?? 0} ${kindLabel.toLowerCase()} · ${stats.overdue?.count ?? 0} with overdue payments`
    : "";

  return (
    <div className="page-content">
      <ExportCSVModal show={isExportCSV} onCloseClick={() => setIsExportCSV(false)} data={"null"} />

      <ClientModal
        show={clientModal}
        clientOption={filterParams.buyer_type}
        onCloseClick={() => setClientModal(false)}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteClient}
        onCloseClick={() => setDeleteModal(false)}
        isDeleting={isDeleting}
      />

      <Container fluid>
        <AppBreadcrumb />

        <div className="d-flex align-items-start flex-wrap gap-3 mb-3">
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <h1 className="mb-0" style={{ fontSize: 27, fontWeight: 650, letterSpacing: "-0.5px" }}>
              {kindLabel}
            </h1>
            <div className="text-muted mt-1" style={{ fontSize: 13.5 }}>{headline}</div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setIsExportCSV(true)}>
              <i className="ri-file-download-line align-bottom me-1"></i> Export CSV
            </button>
            {canDelete && (
              <button type="button" className="btn btn-primary" onClick={() => setClientModal(true)}>
                <i className="ri-add-line align-bottom me-1"></i> New client
              </button>
            )}
          </div>
        </div>

        <ClientStatTiles
          stats={stats}
          kindLabel={kindLabel}
          paymentStatus={filterParams.payment_status || ""}
          linked={!!filterParams.linked}
          onSelectStatus={(value) => updateFilter("payment_status", value)}
          onSelectLinked={(value) => updateFilter("linked", value)}
        />

        <Card className="mb-3">
          <ClientFilterBar
            searchValue={searchInput}
            onSearchChange={setSearchInput}
            paymentStatus={filterParams.payment_status || ""}
            onPaymentStatusChange={(v) => updateFilter("payment_status", v)}
            phase={filterParams.phase || ""}
            onPhaseChange={(v) => updateFilter("phase", v)}
            linked={!!filterParams.linked}
            onLinkedChange={(v) => updateFilter("linked", v)}
            phases={phases}
            hasAnyFilter={hasAnyFilter}
            onClear={handleClearFilters}
          />

          <div style={{ position: "relative" }}>
            {/* Stale-while-refetching overlay — covers the switch-kind and
                search cases, where hasClients stays true (previous fetch's
                data) throughout the new request, so the plain
                loading && !hasClients branch below never fires. */}
            {loading && hasClients && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(255,255,255,0.65)",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  paddingTop: 48,
                  zIndex: 2,
                }}
              >
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {loading && !hasClients ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : hasClients ? (
              <>
                <ClientsTable
                  clients={clients}
                  ordering={filterParams.ordering || DEFAULT_ORDERING}
                  onSortChange={(ordering) => updateFilter("ordering", ordering)}
                  onRowClick={handleRowClick}
                  canDelete={canDelete}
                  onDeleteRequest={handleDeleteRequest}
                  formatCurrency={formatCurrency}
                />
                <ClientPaginationFooter
                  count={count}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  nextPage={nextPage}
                  previousPage={previousPage}
                  loading={loading}
                  onPrev={(p) => p && setPage(p)}
                  onNext={(p) => p && setPage(p)}
                />
              </>
            ) : emptyFromFilters ? (
              <div className="text-center py-5">
                <h5 className="text-muted mb-2">No clients match these filters</h5>
                <p className="text-muted mb-0">Try clearing the payment state or data filter.</p>
              </div>
            ) : (
              <div className="text-center py-5">
                <h5 className="text-muted mb-2">No {kindLabel.toLowerCase()} yet</h5>
                <p className="text-muted mb-3">Add your first client to get started.</p>
                {canDelete && (
                  <button type="button" className="btn btn-primary" onClick={() => setClientModal(true)}>
                    <i className="ri-add-line align-bottom me-1"></i> New client
                  </button>
                )}
              </div>
            )}
          </div>
        </Card>
      </Container>
    </div>
  );
};

export default ClientsList;
